import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Building2, Users, GraduationCap, Copy, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function ForSchools() {
  const [data, setData] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [form, setForm] = useState({ name: "", seats: 30 });
  const [joinId, setJoinId] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/school/me");
      setData(data);
      if (data.school) {
        try {
          const c = await api.get("/school/cohorts");
          setCohorts(c.data.cohorts);
        } catch { setCohorts([]); }
      }
    } catch { setData({ school: null, students: [] }); }
  };
  useEffect(() => { load(); }, []);

  const createSchool = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/school/create", form);
      toast.success("School created");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const join = async (e) => {
    e.preventDefault();
    try {
      await api.post("/school/join", { school_id: joinId });
      toast.success("Joined school");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copyId = () => {
    navigator.clipboard.writeText(data.school.school_id);
    toast.success("School ID copied");
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setLoading(true);
    try {
      const { data: res } = await api.post("/school/roster/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res);
      toast.success(`Added ${res.count} students`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadCreds = () => {
    if (!uploadResult?.created?.length) return;
    const rows = [["email", "name", "temp_password"], ...uploadResult.created.map((c) => [c.email, c.name, c.temp_password])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "roster_credentials.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="max-w-6xl mx-auto p-8">Loading…</div>;

  if (!data.school) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <div className="text-center">
          <span className="nb-badge nb-tag-sky"><Building2 className="w-3.5 h-3.5" /> For Schools</span>
          <h1 className="text-5xl font-extrabold mt-3">School licensing, made easy</h1>
          <p className="text-neutral-600 mt-2">Roster students, watch cohorts grow, pay per seat.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="nb-card p-6">
            <h2 className="text-2xl font-extrabold">Create a school</h2>
            <p className="text-sm text-neutral-600">You'll become admin.</p>
            <form onSubmit={createSchool} className="space-y-3 mt-4">
              <input data-testid="school-name" className="nb-input" placeholder="School name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <input data-testid="school-seats" className="nb-input" type="number" min={1} max={5000} placeholder="Seats" value={form.seats} onChange={(e) => setForm({ ...form, seats: Number(e.target.value) })} required />
              <button data-testid="school-create-btn" disabled={loading} className="nb-btn w-full">{loading ? "Creating…" : "Create school"}</button>
            </form>
          </div>
          <div className="nb-card p-6 bg-[var(--bg-highlight)]">
            <h2 className="text-2xl font-extrabold">Join a school</h2>
            <p className="text-sm text-neutral-600">Ask your admin for the school ID.</p>
            <form onSubmit={join} className="space-y-3 mt-4">
              <input data-testid="school-join-id" className="nb-input" placeholder="School ID (sch_...)" value={joinId} onChange={(e) => setJoinId(e.target.value)} required />
              <button data-testid="school-join-btn" className="nb-btn nb-btn-secondary w-full">Join school</button>
            </form>
            <div className="mt-6 text-sm">
              Need a paid seat plan? <Link to="/pricing" className="font-bold underline">See school pricing</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const s = data.school;
  const avg = data.students.length ? Math.round(data.students.reduce((a, b) => a + (b.avg_mastery || 0), 0) / data.students.length) : 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-8">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <span className="nb-badge nb-tag-sun">{s.name}</span>
          <h1 className="text-4xl font-extrabold mt-2">School dashboard</h1>
        </div>
        <button data-testid="copy-school-id" onClick={copyId} className="nb-btn nb-btn-secondary">
          <Copy className="w-4 h-4" /> Copy school ID
        </button>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Stat label="Seats" value={s.seats} icon={<Building2 />} bg="bg-white" />
        <Stat label="Students" value={data.students.length} icon={<Users />} bg="bg-[var(--sun)]" />
        <Stat label="Avg mastery" value={`${avg}%`} icon={<GraduationCap />} bg="bg-[var(--mint)]" />
        <Stat label="Seats left" value={Math.max(0, s.seats - data.students.length)} icon={<Users />} bg="bg-white" />
      </div>

      <div className="nb-card p-6 bg-[var(--bg-highlight)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-extrabold">Bulk roster upload</h2>
            <p className="text-sm text-neutral-600 max-w-md">
              Upload a CSV with headers <code className="bg-white border border-black rounded px-1">email,name</code>.
              We'll create student accounts and hand you their temporary passwords.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <input ref={fileRef} data-testid="roster-file" type="file" accept=".csv,text/csv" onChange={uploadCsv} className="hidden" />
            <button
              data-testid="roster-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={loading}
              className="nb-btn"
            >
              <Upload className="w-4 h-4" /> {loading ? "Uploading…" : "Upload CSV"}
            </button>
            {uploadResult && uploadResult.created.length > 0 && (
              <button data-testid="download-creds" onClick={downloadCreds} className="nb-btn nb-btn-accent">
                <Download className="w-4 h-4" /> Download credentials
              </button>
            )}
          </div>
        </div>
        {uploadResult && (
          <div className="mt-4 text-sm">
            <div>Created: <b>{uploadResult.count}</b> students · Skipped (already existed): <b>{uploadResult.skipped.length}</b></div>
          </div>
        )}
      </div>

      <div className="nb-card p-6">
        <h2 className="text-2xl font-extrabold">Students</h2>
        {data.students.length === 0 ? (
          <p className="text-neutral-600 mt-2 text-sm">No students yet. Share your school ID <code>{s.school_id}</code> with students so they can join.</p>
        ) : (
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b-2 border-black">
                  <th className="py-2">Name</th><th>Email</th><th>Grade</th><th>Attempts</th><th>Mastery</th>
                </tr>
              </thead>
              <tbody>
                {data.students.map((st) => (
                  <tr key={st.user_id} data-testid={`student-${st.user_id}`} className="border-b border-neutral-200">
                    <td className="py-2 font-bold">{st.name}</td>
                    <td className="text-neutral-600">{st.email}</td>
                    <td className="text-neutral-600">{st.grade ? `Grade ${st.grade}` : "—"}</td>
                    <td>{st.attempts}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-neutral-200 rounded-full border border-black overflow-hidden">
                          <div className="h-full bg-[var(--mint)]" style={{ width: `${st.avg_mastery}%` }} />
                        </div>
                        <span className="text-xs font-bold">{st.avg_mastery}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cohorts.length > 0 && (
        <div className="nb-card p-6">
          <div className="flex justify-between items-end flex-wrap gap-3">
            <div>
              <span className="nb-badge nb-tag-sky">Cohort leaderboard</span>
              <h2 className="text-2xl font-extrabold mt-2">Grade vs. grade</h2>
              <p className="text-sm text-neutral-600">Ranked by average mastery — share it in class.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {cohorts.map((c) => {
              const best = Math.max(...cohorts.map((x) => x.avg_mastery)) || 1;
              const pct = Math.round((c.avg_mastery / best) * 100);
              const podium = c.rank <= 3;
              return (
                <div
                  key={c.cohort}
                  data-testid={`cohort-${c.cohort}`}
                  className={`p-4 border-2 border-black rounded-xl relative ${
                    c.rank === 1 ? "bg-[var(--sun)]" : c.rank === 2 ? "bg-white" : c.rank === 3 ? "bg-[var(--bg-highlight)]" : "bg-white"
                  }`}
                >
                  <div className="absolute -top-3 -left-2 w-10 h-10 rounded-full border-2 border-black bg-white flex items-center justify-center text-lg shadow-[3px_3px_0_0_#1C1C1C]">
                    {c.medal || `#${c.rank}`}
                  </div>
                  <div className="text-xs uppercase font-bold text-neutral-500 ml-8">{c.cohort}</div>
                  <div className="text-3xl font-extrabold ml-8">{c.avg_mastery}%</div>
                  <div className="text-xs ml-8">avg mastery {podium && "· podium"}</div>
                  <div className="h-2 mt-2 bg-neutral-200 rounded-full border-2 border-black overflow-hidden">
                    <div className={`h-full ${c.rank === 1 ? "bg-[var(--coral)]" : "bg-[var(--mint)]"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-center text-xs">
                    <div><div className="text-lg font-extrabold">{c.students}</div><div>students</div></div>
                    <div><div className="text-lg font-extrabold">{c.avg_streak}🔥</div><div>avg streak</div></div>
                    <div><div className="text-lg font-extrabold">{c.total_attempts}</div><div>attempts</div></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon, bg }) {
  return (
    <div className={`nb-card p-5 ${bg}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase font-bold text-neutral-600">{label}</div>
        <div className="w-8 h-8 rounded-lg border-2 border-black flex items-center justify-center bg-white">{icon}</div>
      </div>
      <div className="text-3xl font-extrabold mt-2">{value}</div>
    </div>
  );
}
